import { useState, useEffect, useMemo, useCallback } from "react";
import { getDevices, getSharedDevices } from "../services/api";
import { useSocial } from "../contexts/SocialContext";

/**
 * Hook managing the "cast to device" lifecycle via WebSocket ACKs.
 *
 * States: idle → loading → ready → active → idle
 *
 * All state transitions are driven by `live:ack` and `live:device_state`
 * events from the firmware, relayed through SocialContext. No REST polling.
 *
 * @param {boolean} enabled - Whether to fetch devices
 * @returns {{ devices, castState, liveDeviceState, castLoad, castPlay, castPause, castSkip, castStop, cancelCast, isCasting }}
 */
export default function useDeviceCast(enabled = true) {
  const [devices, setDevices] = useState([]);
  // Timers mates have shared with you (#171); sent to one at a time.
  const [sharedDevices, setSharedDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [error, setError] = useState(null);
  // Local cast lifecycle: idle | loading (waiting for load ACK)
  const [localState, setLocalState] = useState("idle");

  const social = useSocial();
  const liveDeviceState = social?.liveDeviceState;
  const devicePresence = social?.devicePresence;

  // Fetch linked devices on mount
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getDevices()
      .then((data) => {
        if (cancelled) return;
        setDevices(data);
        if (data.length === 1) setSelectedDeviceId(data[0].id);
      })
      .catch(() => {});
    getSharedDevices()
      .then((data) => !cancelled && setSharedDevices(data || []))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled]);

  // Fresh lists, including who is using each timer right now. Called just
  // before a send, so a timer someone else took since this screen opened is
  // caught before a session is made for it.
  const refreshDevices = useCallback(async () => {
    const [own, shared] = await Promise.all([
      getDevices().catch(() => null),
      getSharedDevices().catch(() => null),
    ]);
    if (own) setDevices(own);
    if (shared) setSharedDevices(shared);
    return { devices: own || [], sharedDevices: shared || [] };
  }, []);

  // Derive castState from device heartbeat + local loading flag
  const castState = useMemo(() => {
    if (localState === "loading") return "loading";
    if (!liveDeviceState?.state) return "idle";

    const s = liveDeviceState.state;
    if (s === "Ready") return "ready";
    if (s === "Inactive" || s === "Clear" || s === "Complete") return "idle";
    // Work, Rest, RndRest, Cool, Paused, Active → all "active"
    return "active";
  }, [localState, liveDeviceState]);

  const isCasting = castState !== "idle";
  // Your own timer's presence, not the latest event: a mate's shared timer
  // reports presence too, and its going offline isn't yours.
  const presenceByDevice = social?.presenceByDevice;
  const ownTimerId = selectedDeviceId ?? devices[0]?.id;
  const isOnline =
    ownTimerId != null && presenceByDevice?.[Number(ownTimerId)] != null
      ? presenceByDevice[Number(ownTimerId)]
      : devicePresence?.online !== false;

  // Cast actions — delegate to SocialContext, track local loading state
  const castLoad = useCallback(
    async (plan, sessionRef, title) => {
      if (!social?.castLoad) return;
      setLocalState("loading");
      setError(null);
      try {
        await social.castLoad(plan, sessionRef, title);
        // ACK received → device is in Ready state; castState will derive from heartbeat
        setLocalState("idle");
      } catch (err) {
        setError(err.message || "Failed to send to device");
        setLocalState("idle");
      }
    },
    [social],
  );

  const castPlay = useCallback(async () => {
    try { await social?.castPlay(); } catch (err) { setError(err.message); }
  }, [social]);

  const castPause = useCallback(async () => {
    try { await social?.castPause(); } catch (err) { setError(err.message); }
  }, [social]);

  const castSkip = useCallback(async (direction = 1) => {
    try { await social?.castSkip(direction); } catch (err) { setError(err.message); }
  }, [social]);

  const castStop = useCallback(async () => {
    try { await social?.castStop(); } catch (err) { setError(err.message); }
  }, [social]);

  const cancelCast = useCallback(() => {
    setLocalState("idle");
    setError(null);
    // Optionally tell device to stop
    social?.castStop?.().catch(() => {});
  }, [social]);

  return {
    devices,
    sharedDevices,
    refreshDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    castState,
    isCasting,
    isOnline,
    liveDeviceState,
    devicePresence,
    error,
    castLoad,
    castPlay,
    castPause,
    castSkip,
    castStop,
    cancelCast,
  };
}
